import { sfxPlay } from "@/assets/audios/sfx"
import type { Skill } from "@/components/SkillDialog"

// 统一的引导进度 key
export const GUIDE_PROGRESS_KEY = "goGuideProgress"

// 引导阶段枚举
export enum GuideStage {
    NONE = 0, // 未开始
    START_DIALOGUE = 1, // 已完成开始游戏对话
    GUIDE_DIALOGUE = 2, // 已完成引导对话
    SKILL_GUIDE = 3, // 已完成技能选择引导
    SKILL_USE_GUIDE = 4, // 已完成技能使用引导
}

/**
 * 防抖函数
 * @param func 要防抖的函数
 * @param wait 等待时间（毫秒），默认 1000ms
 * @returns 防抖后的函数
 */
export const debounce = <T extends (...args: unknown[]) => unknown>(
    func: T,
    wait: number = 1000
): ((...args: Parameters<T>) => void) => {
    let timeout: ReturnType<typeof setTimeout> | null = null

    return (...args: Parameters<T>) => {
        if (timeout) {
            clearTimeout(timeout)
        }

        timeout = setTimeout(() => {
            func(...args)
        }, wait)
    }
}

/**
 * 节流函数
 * @param func 要节流的函数
 * @param wait 等待时间（毫秒），默认 1000ms
 * @returns 节流后的函数
 */
export const throttle = <T extends (...args: unknown[]) => unknown>(
    func: T,
    wait: number = 1000
): ((...args: Parameters<T>) => void) => {
    let timer: ReturnType<typeof setTimeout> | null = null

    return (...args: Parameters<T>) => {
        if (timer !== null) return
        func(...args)
        timer = setTimeout(() => {
            timer = null
        }, wait)
    }
}

// 数字转中文
export const numberToChinese = (num: number): string => {
    const digits = ["零", "一", "二", "三", "四", "五", "六", "七", "八", "九"]

    // 分离整数部分和小数部分
    const integerPart = Math.floor(num)
    const decimalPart = num - integerPart

    // 转换整数部分
    let result = ""

    if (integerPart === 0) {
        result = "零"
    } else if (integerPart < 10) {
        result = digits[integerPart]
    } else if (integerPart < 20) {
        // 10-19: 十、十一、十二...
        result = integerPart === 10 ? "十" : "十" + digits[integerPart % 10]
    } else if (integerPart < 100) {
        // 20-99: 二十、二十一...
        const tens = Math.floor(integerPart / 10)
        const ones = integerPart % 10
        result = digits[tens] + "十"
        if (ones > 0) {
            result += digits[ones]
        }
    } else {
        // 100+: 一百、一百零一...
        const hundreds = Math.floor(integerPart / 100)
        const remainder = integerPart % 100
        result = digits[hundreds] + "百"

        if (remainder === 0) {
            // 整百
        } else if (remainder < 10) {
            // 101-109: 一百零一...
            result += "零" + digits[remainder]
        } else {
            // 110+: 一百一十...
            const tens = Math.floor(remainder / 10)
            const ones = remainder % 10
            result += digits[tens] + "十"
            if (ones > 0) {
                result += digits[ones]
            }
        }
    }

    // 处理小数部分（只可能是 0.5）
    if (decimalPart > 0) {
        result += "又二分之一"
    }

    return result
}

// 获取引导进度阶段
export const getGuideProgress = (): GuideStage => {
    try {
        const value = localStorage.getItem(GUIDE_PROGRESS_KEY)
        if (value === null) {
            return GuideStage.NONE
        }
        const stage = parseInt(value, 10)
        return isNaN(stage) ? GuideStage.NONE : (stage as GuideStage)
    } catch (error) {
        return GuideStage.NONE
    }
}

// 设置引导进度阶段
export const setGuideProgress = (stage: GuideStage): void => {
    try {
        localStorage.setItem(GUIDE_PROGRESS_KEY, String(stage))
    } catch (error) {
        console.warn("无法保存到 localStorage:", error)
    }
}

// 检查是否已完成指定阶段
export const hasCompletedStage = (stage: GuideStage): boolean => {
    const currentStage = getGuideProgress()
    return currentStage >= stage
}

// 兼容旧的 getStorageValue 函数（用于非引导相关的存储）
export const getStorageValue = (key: string): boolean => {
    try {
        return localStorage.getItem(key) === "true"
    } catch (error) {
        return false
    }
}

// 兼容旧的 setStorageValue 函数（用于非引导相关的存储）
export const setStorageValue = (key: string, value: boolean): void => {
    try {
        localStorage.setItem(key, value ? "true" : "false")
    } catch (error) {
        console.warn("无法保存到 localStorage:", error)
        // 静默失败，不影响主流程
    }
}

export const playSkillFrontSound = (skill: Skill) => {
    switch (skill) {
        case "skill1":
            sfxPlay("SKILL1_1")
            break
        case "skill2":
            sfxPlay("SKILL2_1")
            break
        case "skill3":
            sfxPlay("SKILL3_1")
            break
        case "skill4":
            sfxPlay("SKILL4_1")
            break
        case "skill5":
            sfxPlay("SKILL5_1")
            break
        default:
            break
    }
}

export const playSkillBackSound = (skill: Skill) => {
    switch (skill) {
        case "skill1":
            sfxPlay("SKILL1_2")
            break
        case "skill2":
            sfxPlay("SKILL2_2")
            break
        case "skill3":
            sfxPlay("SKILL3_2")
            break
        case "skill4":
            sfxPlay("SKILL4_2")
            break
        case "skill5":
            sfxPlay("SKILL5_2")
            break
    }
}
